import { useNavigate, Link } from "react-router-dom"
import { CheckCircle2, ArrowRight, BookOpen, Scaling, Users, Globe, Building2, GraduationCap, MapPin, Award, Target, Landmark } from "lucide-react"
import { Button } from "../../../components/ui/button"
import { Helmet } from "react-helmet-async"
import heroImage from "../../../assets/TopHeroImages/1.jpg"
/**
 * DroitEcoGestion Component
 * 
 * A premium, editorial-style page for the Faculty of Law, Economics, and Management.
 * Follows the visual language of the University of Ngozi (UNG) brand.
 * Shadow-free design for a cleaner, modern look.
 */
export default function DroitEcoGestion() {
    const navigate = useNavigate()
    return (
        <div className="min-h-screen bg-background font-sans text-foreground selection:bg-accent selection:text-accent-foreground">
            <Helmet>
                <title>Faculté de Droit, Économie & Gestion (FDSEG) - Université de Ngozi</title>
                <meta name="description" content="Découvrez la FDSEG de l'Université de Ngozi. Formations d'excellence en Droit, Sciences Économiques et Gestion sur nos campus de Ngozi et Bujumbura." />
                <meta name="keywords" content="FDSEG, Droit, Économie, Gestion, Université de Ngozi, Burundi, Master Droit Public, Master Banque" />
            </Helmet>

            {/* Hero Section with Immersive Visuals */}
            <header className="relative min-h-[60vh] flex items-center bg-primary overflow-hidden">
                {/* Background Image with Overlay */}
                <div className="absolute inset-0 z-0">
                    <img
                        src={heroImage}
                        alt="FDSEG Faculty Building"
                        className="w-full h-full object-cover opacity-40 mix-blend-luminosity scale-105 hover:scale-100 transition-transform duration-[10s] ease-out"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/80 to-transparent" />
                </div>

                <div className="container relative z-10 py-20">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-4 mb-8 translate-y-4 animate-in fade-in duration-700 fill-mode-forwards">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black">
                                Faculté d'Excellence
                            </span>
                            <div className="h-px w-12 bg-accent/50" />
                            <span className="text-[10px] text-primary-foreground/60 uppercase tracking-widest font-bold">Est. 1999</span>
                        </div>

                        <h1 className="heading-1 mb-8 leading-[0.95] font-serif tracking-tight text-white translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-200">
                            <span className="block italic font-light text-accent/90">L'Excellence au service du</span>
                            <span className="block text-white mt-2">Droit & de la Gestion</span>
                        </h1>

                        <div className="border-l-4 border-accent pl-8 mb-12 max-w-2xl translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-500">
                            <p className="text-lg md:text-xl font-light leading-relaxed text-primary-foreground/90">
                                Contribuer à l'amélioration des conditions de vie par le renforcement de la formation universitaire et de la recherche appliquée.
                            </p>
                        </div>

                        <div className="flex flex-wrap gap-6 items-center translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-700">
                            <Button onClick={() => navigate("/admission")} className="bg-accent text-accent-foreground px-10 py-7 text-sm font-black transition-all duration-300 border-b-4 border-black/20 rounded-xl">
                                Déposer ma Candidature
                                <ArrowRight className="w-5 h-5 ml-3" />
                            </Button>
                            <Button onClick={() => document.getElementById("presentation")?.scrollIntoView({ behavior: "smooth" })} variant="ghost" className="text-white border-2 border-white/20 hover:bg-white/10 hover:border-white px-10 py-7 text-sm font-black rounded-xl backdrop-blur-sm">
                                Voir les Programmes
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Bottom Scroll Indicator Decorative */}
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce opacity-20">
                    <div className="w-px h-12 bg-white" />
                </div>
            </header>

            {/* Editorial Stats Bar */}
            <section className="bg-secondary/5 border-b border-border relative overflow-hidden">
                <div className="container py-8 flex flex-wrap items-center justify-between gap-12">
                    {[
                        { val: "25+", label: "Années d'Expérience", icon: Award },
                        { val: "2", label: "Campus Stratégiques", icon: Landmark },
                        { val: "5", label: "Masters Spécialisés", icon: GraduationCap },
                        { val: "LMD", label: "Système Académique", icon: Target }
                    ].map((stat, i) => (
                        <div key={i} className="flex items-center gap-4 group">
                            <div className="p-3 bg-primary/5 rounded-xl group-hover:bg-accent/10 transition-colors">
                                <stat.icon className="w-6 h-6 text-primary group-hover:text-accent transition-colors" />
                            </div>
                            <div>
                                <div className="text-2xl font-black text-primary leading-none mb-1">{stat.val}</div>
                                <div className="text-[10px] text-muted-foreground uppercase font-black tracking-widest">{stat.label}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Main Article Layout */}
            <article className="container section-padding">
                <div className="grid lg:grid-cols-[280px_1fr] gap-16 lg:gap-24">
                    {/* Floating Sidebar Navigation */}
                    <aside className="lg:sticky lg:top-32 lg:self-start hidden lg:block">
                        <div className="space-y-12">
                            <nav className="space-y-2">
                                <p className="text-[11px] uppercase tracking-[0.25em] font-black text-muted-foreground mb-8 pb-4 border-b-2 border-primary/10">
                                    Navigation
                                </p>
                                {[
                                    { id: "presentation", label: "Présentation" },
                                    { id: "mission", label: "Mission & Vision" },
                                    { id: "departements", label: "Départements" },
                                    { id: "masters", label: "Programmes Master" },
                                    { id: "implantation", label: "Implantation" }
                                ].map((item, i) => (
                                    <a
                                        key={i}
                                        href={`#${item.id}`}
                                        className="group flex items-center justify-between py-3 text-xs font-black text-muted-foreground hover:text-primary transition-all pr-4 border-r-2 border-transparent hover:border-primary"
                                    >
                                        <div className="flex items-center gap-4">
                                            <span className="text-[10px] font-serif italic text-accent opacity-0 group-hover:opacity-100 transition-opacity">0{i + 1}</span>
                                            <span className="uppercase tracking-widest">{item.label}</span>
                                        </div>
                                        <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                                    </a>
                                ))}
                            </nav>

                            <div className="bg-primary p-8 rounded-2xl text-primary-foreground relative overflow-hidden group">
                                <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full -mr-12 -mt-12 group-hover:scale-150 transition-transform duration-700" />
                                <h4 className="text-[10px] uppercase tracking-[0.2em] font-black mb-6 text-accent">Info Officielle</h4>
                                <p className="text-xs font-light leading-relaxed mb-6 opacity-80">
                                    Institution privée créée en 1999, agréée par l’Ordonnance No 610/650 du 22 Octobre 1999.
                                </p>
                                <Button className="w-full bg-white/10 hover:bg-white/20 text-white text-[10px] font-black border border-white/20 py-5 rounded-xl uppercase tracking-widest transition-all">
                                    Consulter les statuts
                                </Button>
                            </div>
                        </div>
                    </aside>

                    {/* Content Body */}
                    <main className="space-y-24">
                        {/* 01. Presentation */}
                        <section id="presentation" className="scroll-mt-32">
                            <div className="flex flex-col gap-8">
                                <div className="space-y-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-1 bg-accent rounded-full" />
                                        <span className="text-xs font-black uppercase tracking-[0.3em] text-primary">Le Mot du Doyen</span>
                                    </div>
                                    <h2 className="heading-2 font-serif italic">Former les cadres du changement durable.</h2>
                                </div>

                                <div className="grid md:grid-cols-2 gap-12">
                                    <div className="prose-base text-muted-foreground/90 font-light leading-relaxed space-y-6">
                                        <p>
                                            L’Université de Ngozi, institution d’enseignement supérieur de type communautaire, s’est donnée pour mission d'être un catalyseur de développement au Burundi.
                                        </p>
                                        <p>
                                            Plus qu'une simple faculté, la <span className="text-primary font-bold">FDSEG</span> est une pépinière de leaders capables d’appréhender les défis complexes de notre société et d’y apporter des solutions innovantes et éthiques.
                                        </p>
                                    </div>
                                    <div className="bg-secondary/5 rounded-3xl p-8 border border-border/50 relative">
                                        <h3 className="text-sm font-black uppercase tracking-widest mb-6 flex items-center gap-3">
                                            <Target className="w-5 h-5 text-accent" />
                                            Nos Objectifs Fondamentaux
                                        </h3>
                                        <ul className="space-y-6">
                                            <li className="flex gap-4">
                                                <div className="w-6 h-6 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-[10px] font-black flex-shrink-0">1</div>
                                                <p className="text-sm font-medium">Réduction de la pauvreté par le renforcement des compétences universitaires.</p>
                                            </li>
                                            <li className="flex gap-4">
                                                <div className="w-6 h-6 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-[10px] font-black flex-shrink-0">2</div>
                                                <p className="text-sm font-medium">Exploration de solutions adaptées aux spécificités des différents milieux Burundais.</p>
                                            </li>
                                            <li className="flex gap-4">
                                                <div className="w-6 h-6 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-[10px] font-black flex-shrink-0">3</div>
                                                <p className="text-sm font-medium">Promotion de l'éducation morale et humaine de la jeunesse estudiantine.</p>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* 02. Mission & Vision - Visual Impact */}
                        <section id="mission" className="scroll-mt-32">
                            <div className="bg-primary rounded-[3rem] p-12 lg:p-20 text-white relative overflow-hidden group">
                                <div className="absolute top-0 right-0 w-96 h-96 bg-accent/20 rounded-full blur-[100px] -mr-32 -mt-32 transition-transform duration-1000 group-hover:scale-110" />
                                <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-[80px] -ml-20 -mb-20" />

                                <div className="relative z-10 grid md:grid-cols-2 gap-16 items-center">
                                    <div className="space-y-8">
                                        <div className="inline-block px-4 py-2 bg-white/10 rounded-full text-[10px] font-black tracking-widest uppercase border border-white/10">
                                            Notre Identité
                                        </div>
                                        <h2 className="heading-3 text-white font-serif italic">Porter une vision, <br /><span className="text-accent underline decoration-accent/30 decoration-8 underline-offset-4">incarner l'excellence</span>.</h2>
                                        <p className="text-white/70 font-light leading-relaxed">
                                            Nous aspirons à être reconnus comme l'acteur majeur de la formation des leaders burundais, prêts à répondre aux enjeux contemporains par l'innovation et la recherche de pointe.
                                        </p>
                                    </div>
                                    <div className="grid gap-6">
                                        <div className="p-8 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 hover:bg-white/10 transition-colors">
                                            <h4 className="text-accent font-black uppercase tracking-widest text-xs mb-4 flex items-center gap-2">
                                                <div className="w-1 h-4 bg-accent" /> Mission Académique
                                            </h4>
                                            <p className="text-sm text-white/90 leading-relaxed font-light">
                                                Formation de haute qualité, recherche fondamentale et appliquée, et préparation éthique aux défis professionnels.
                                            </p>
                                        </div>
                                        <div className="p-8 bg-accent rounded-2xl border border-accent hover:brightness-105 transition-all group/card cursor-default">
                                            <h4 className="text-primary font-black uppercase tracking-widest text-xs mb-4 flex items-center gap-2">
                                                <div className="w-1 h-4 bg-primary" /> Vision 2030
                                            </h4>
                                            <p className="text-primary font-bold leading-relaxed text-sm">
                                                Devenir le pôle de référence pour l'innovation administrative et le développement durable dans la sous-région.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* 03. Departments - Modern Grid */}
                        <section id="departements" className="scroll-mt-32">
                            <div className="space-y-12">
                                <div className="flex items-end justify-between">
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-3 text-accent">
                                            <BookOpen className="w-5 h-5" />
                                            <span className="text-xs font-black uppercase tracking-widest">Offre Académique</span>
                                        </div>
                                        <h2 className="heading-2">Structure de Formation</h2>
                                    </div>
                                    <p className="max-w-xs text-xs text-muted-foreground uppercase tracking-widest leading-relaxed hidden md:block">
                                        Une organisation rigoureuse articulée autour de 3 pôles d'expertise.
                                    </p>
                                </div>

                                <div className="grid md:grid-cols-3 gap-8">
                                    {[
                                        {
                                            title: "Droit",
                                            icon: Landmark,
                                            desc: "Étude approfondie des normes juridiques pour une justice équitable.",
                                            tags: ["Droit Public", "Droit Privé"]
                                        },
                                        {
                                            title: "Économie",
                                            icon: Scaling,
                                            desc: "Analyse des systèmes et planification pour un développement solidaire.",
                                            tags: ["Éco-Politique", "Planfication", "Coopératives"]
                                        },
                                        {
                                            title: "Gestion",
                                            icon: Building2,
                                            desc: "Maîtrise des outils de management et d'audit pour l'entreprise moderne.",
                                            tags: ["Admin. Affaires", "Comptabilité", "Audit"]
                                        }
                                    ].map((dept, i) => (
                                        <div key={i} className="group p-10 bg-white border border-border rounded-3xl hover:border-accent transition-all duration-500 hover:-translate-y-2">
                                            <div className="w-14 h-14 bg-secondary/10 rounded-2xl flex items-center justify-center text-primary mb-8 group-hover:bg-primary group-hover:text-white transition-all duration-500">
                                                <dept.icon className="w-7 h-7" />
                                            </div>
                                            <h3 className="text-2xl font-serif font-bold mb-4">{dept.title}</h3>
                                            <p className="text-sm text-muted-foreground leading-relaxed mb-8 opacity-80">{dept.desc}</p>
                                            <div className="flex flex-wrap gap-2">
                                                {dept.tags.map((tag, j) => (
                                                    <span key={j} className="px-3 py-1 bg-secondary/30 text-[9px] font-black uppercase tracking-widest text-primary rounded-full group-hover:bg-accent/20 group-hover:text-amber-900 transition-colors">
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </section>

                        {/* 04. Masters - Premium List */}
                        <section id="masters" className="scroll-mt-32">
                            <div className="bg-gradient-to-br from-primary via-primary/95 to-primary/90 rounded-[3rem] p-12 lg:p-20 text-white relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-full h-full opacity-10 pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/graphy.png')]" />

                                <div className="relative z-10 flex flex-col md:flex-row gap-16 items-start">
                                    <div className="md:w-1/3">
                                        <GraduationCap className="w-16 h-16 text-accent mb-8" />
                                        <h2 className="heading-3 text-white font-serif mb-6">Expertise de Second Cycle</h2>
                                        <p className="text-white/60 font-light text-sm leading-relaxed mb-8">
                                            Nos masters sont agréés et reconnus pour leur solidité académique, visant à former des praticiens de haut vol pour le marché national et international.
                                        </p>
                                        <Button className="bg-accent text-accent-foreground font-black px-8 py-6 rounded-xl text-xs uppercase tracking-widest hover:brightness-110 active:scale-95 transition-all">
                                            Guide des Masters (PDF)
                                        </Button>
                                    </div>
                                    <div className="md:w-2/3 grid gap-4 w-full">
                                        {[
                                            { title: "Master en Droit Public", category: "Juridique", href: "/masters/droit-public" },
                                            { title: "Master en Droit Privé", category: "Juridique", href: "/masters/droit-prive" },
                                            { title: "Master en Politiques Économiques", category: "Économie", href: "/masters/politiques-economiques" },
                                            { title: "Master en Management des Projets", category: "Gestion", href: "/masters/management-projets" },
                                            { title: "Master en Banque & Microfinance", category: "Finance", href: "/masters/banque-microfinance" }
                                        ].map((master, i) => (
                                            <Link key={i} to={master.href} className="group p-6 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-between hover:bg-white/10 hover:translate-x-4 transition-all duration-300 cursor-pointer">
                                                <div className="flex items-center gap-6">
                                                    <span className="text-accent font-serif italic text-2xl opacity-40 group-hover:opacity-100 transition-opacity">0{i + 1}</span>
                                                    <h4 className="text-lg font-bold font-serif">{master.title}</h4>
                                                </div>
                                                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white/40 border border-white/20 px-3 py-1 rounded-full group-hover:text-accent group-hover:border-accent transition-colors">
                                                    {master.category}
                                                </span>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* 05. Implantation - Location Cards */}
                        <section id="implantation" className="scroll-mt-32">
                            <div className="space-y-12">
                                <div className="space-y-4">
                                    <div className="flex items-center gap-3 text-accent">
                                        <MapPin className="w-5 h-5" />
                                        <span className="text-xs font-black uppercase tracking-widest">Territoire & Réseau</span>
                                    </div>
                                    <h2 className="heading-2">Notre Rayonnement</h2>
                                </div>

                                <div className="grid md:grid-cols-2 gap-12">
                                    <div className="group relative rounded-3xl overflow-hidden bg-white border border-border transition-all hover:border-accent">
                                        <div className="p-12 space-y-6">
                                            <div className="flex justify-between items-start">
                                                <h3 className="text-3xl font-serif font-bold text-primary">Campus Ngozi</h3>
                                                <Landmark className="w-10 h-10 text-accent/20 group-hover:text-accent transition-colors duration-500" />
                                            </div>
                                            <div className="space-y-2">
                                                <p className="text-sm font-black uppercase tracking-widest text-muted-foreground">Siège Social</p>
                                                <p className="text-lg font-light flex items-center gap-2">
                                                    <MapPin className="w-4 h-4 text-accent" />
                                                    Quartier Kanyami
                                                </p>
                                            </div>
                                            <div className="pt-6 border-t border-border flex flex-wrap gap-3">
                                                <span className="px-4 py-2 bg-secondary/5 text-[10px] font-black uppercase tracking-[0.2em] rounded-full">Administration Centrale</span>
                                                <span className="px-4 py-2 bg-secondary/5 text-[10px] font-black uppercase tracking-[0.2em] rounded-full">Bibliothèque de Droit</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="group relative rounded-3xl overflow-hidden bg-primary text-white transition-all hover:-translate-y-2 active:scale-[0.98]">
                                        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                                        <div className="p-12 space-y-6 relative z-10">
                                            <div className="flex justify-between items-start">
                                                <h3 className="text-3xl font-serif font-bold text-white">Campus Bujumbura</h3>
                                                <Globe className="w-10 h-10 text-accent/20 group-hover:text-accent transition-colors duration-500" />
                                            </div>
                                            <div className="space-y-2">
                                                <p className="text-sm font-black uppercase tracking-widest text-white/40">Extension Stratégique</p>
                                                <p className="text-lg font-light flex items-start gap-2 leading-relaxed">
                                                    <MapPin className="w-4 h-4 text-accent flex-shrink-0 mt-1" />
                                                    Ntahangwa, Zone Kamenge, <br />Quartier Uwinterekwa
                                                </p>
                                            </div>
                                            <div className="pt-6 border-t border-white/10 flex flex-wrap gap-3">
                                                <span className="px-4 py-2 bg-white/5 text-[10px] font-black uppercase tracking-[0.2em] rounded-full border border-white/10">Pôle Économique</span>
                                                <span className="px-4 py-2 bg-white/5 text-[10px] font-black uppercase tracking-[0.2em] rounded-full border border-white/10">Campus Urbain</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-secondary/10 rounded-[2.5rem] p-12 lg:p-20 border border-border/50">
                                    <div className="grid lg:grid-cols-[1fr_2fr] gap-16 items-center">
                                        <div className="space-y-6">
                                            <h4 className="text-2xl font-serif font-bold">Un Pôle, <br /><span className="text-accent underline underline-offset-8">Deux Destins</span>.</h4>
                                            <p className="text-sm text-muted-foreground leading-relaxed font-light">
                                                La dispense sur deux campus permet de maximiser l'impact du programme tout en réduisant les barrières géographiques et économiques pour nos étudiants.
                                            </p>
                                        </div>
                                        <ul className="grid sm:grid-cols-2 gap-8">
                                            {[
                                                { title: "Accessibilité", desc: "Rayonner au-delà des frontières locales." },
                                                { title: "Besoins Locaux", desc: "Contenus adaptés aux réalités économiques." },
                                                { title: "Optimisation", desc: "Ressources académiques partagées." },
                                                { title: "Collaboration", desc: "Une communauté académique dynamique." }
                                            ].map((point, i) => (
                                                <li key={i} className="flex gap-4">
                                                    <div className="w-px h-12 bg-accent" />
                                                    <div>
                                                        <h5 className="text-xs font-black uppercase tracking-widest mb-1 text-primary">{point.title}</h5>
                                                        <p className="text-xs text-muted-foreground font-light">{point.desc}</p>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </main>
                </div>
            </article>

            {/* CTA Action - commented out*/}
            <footer className="bg-primary text-white border-t-8 border-accent relative overflow-hidden">
                <div className="absolute top-0 right-0 w-1/3 h-full bg-accent/5 -skew-x-12 origin-top-right pointer-events-none" />
                <div className="absolute bottom-0 left-0 w-px h-full bg-white/10 ml-[20%]" />

                <div className="container py-24 text-center relative z-10">
                    <div className="max-w-2xl mx-auto space-y-10">
                        <div className="inline-flex items-center gap-6 mb-4">
                            <div className="h-px w-8 bg-accent" />
                            <span className="text-[11px] uppercase tracking-[0.4em] text-accent font-black">Inscriptions Ouvertes</span>
                            <div className="h-px w-8 bg-accent" />
                        </div>

                        <h2 className="heading-1 leading-tight font-serif italic text-white">
                            Projetez votre ambition <br />vers de <span className="text-accent">nouveaux sommets</span>.
                        </h2>

                        {/* <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                            <Button className="bg-accent text-accent-foreground px-12 py-8 text-sm font-black hover:bg-white hover:text-primary hover:-translate-y-2 transition-all duration-500 rounded-2xl transform">
                                Commencer mon inscription
                                <ArrowRight className="w-5 h-5 ml-3" />
                            </Button>
                            <Button variant="ghost" className="text-white border-2 border-white/20 hover:bg-white/5 px-12 py-8 text-sm font-black rounded-2xl transition-all">
                                Parler à un conseiller
                            </Button>
                        </div> */}

                        <p className="text-white/40 text-[10px] uppercase tracking-[0.2em] font-medium mt-12">
                            © 2024 Faculté de Droit, Économie & Gestion · UNG
                        </p>
                    </div>
                </div>
            </footer>
            
        </div>
    )
}
